import { Button, Card, Chip, DateColumn, Typography } from '@otp/ui';
import { Arrow } from '@otp/icons';
import { getFullName } from 'src/utils/user-data';
import { useAuthContext } from 'src/context/auth.context';
import { useState } from 'react';
import { WttpQuestionnaireResponsesData } from '../types';
import { Status } from 'src/components/wttp/questionnaire-responses/list/table/columns/status';
import { WttpQuestionnaireResponseStatus } from 'src/graphql/generated/graphql';
import { useTranslation } from 'react-i18next';
import { ActionsColumn } from 'src/components/wttp/questionnaire-responses/list/table/columns/actions';
import { WttpQuestionnaireResponseModal } from '../../questionnaire-response-modal';

type WttpQuestionnaireCardProps = {
  questionnaireResponse: WttpQuestionnaireResponsesData;
  buttonVariant?: 'icon' | 'button';
  onClose?: () => void;
};

export const WttpQuestionnaireCard = ({
  questionnaireResponse,
  buttonVariant = 'button',
  onClose,
}: WttpQuestionnaireCardProps) => {
  const { user } = useAuthContext();
  const { t } = useTranslation();

  const [displaySurvey, setDisplaySurvey] = useState(false);

  const getButtonText = () => {
    if (!questionnaireResponse.isActive) {
      return t(
        `wttp.questionnaire-response.list.button-label.${WttpQuestionnaireResponseStatus.Completed.toLowerCase()}`,
      );
    }

    if (questionnaireResponse.status) {
      return t(
        `wttp.questionnaire-response.list.button-label.${questionnaireResponse.status?.toLowerCase()}`,
      );
    }

    return `wttp.questionnaire-response.list.button-label.${WttpQuestionnaireResponseStatus.Started.toLowerCase()}`;
  };

  const onClick = () => {
    setDisplaySurvey(true);
  };

  return (
    <>
      <Card
        className="w-full pb-3 mb-2"
        onClick={buttonVariant === 'icon' ? onClick : undefined}
        isLink={buttonVariant === 'icon'}
      >
        <div className="flex justify-between gap-2">
          <Typography component="div" fontWeight="600" variant="body2">
            {questionnaireResponse.questionnaire?.name}
          </Typography>

          <div className="flex flex-1 justify-end ml-4">
            <Chip
              label={`#${questionnaireResponse.store?.nationalStoreNumber}`}
            />
            <ActionsColumn
              className="ml-2"
              wttpQuestionnaireResponse={questionnaireResponse}
              onActionFinished={() => {}}
            />
          </div>
        </div>
        <div className="flex justify-between mt-2  gap-2">
          {questionnaireResponse.assignedTo?.id ? (
            <Typography
              component="div"
              variant="subtitle1"
              color="#454749"
              fontWeight="bold"
            >
              {t('common.assigned-to')}{' '}
              <Typography component="span" variant="subtitle1">
                <span className="font-semibold">
                  {getFullName(questionnaireResponse.assignedTo)}
                </span>{' '}
                {user?.id === questionnaireResponse.assignedTo.id &&
                  `(${t('common.me')})`}
              </Typography>
            </Typography>
          ) : (
            <Typography component="div" fontSize="small" color="grey">
              {t(`wttp.questionnaire-response.list.not-assigned`)}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight="bold">
            <DateColumn date={questionnaireResponse.startedAt} withTime />
          </Typography>
        </div>
        <div className="flex justify-between mt-4 items-center  gap-2">
          <Status
            status={questionnaireResponse.status}
            wttpQuestionnaireResponse={questionnaireResponse}
          />
          {buttonVariant === 'button' && (
            <Button variant="contained" onClick={onClick}>
              {getButtonText()}
              <Arrow className="rotate-180 ml-2" fill="white" />
            </Button>
          )}
        </div>
      </Card>
      {displaySurvey && (
        <WttpQuestionnaireResponseModal
          questionnaireResponseId={questionnaireResponse.id}
          onClose={() => {
            setDisplaySurvey(false);
            onClose?.();
          }}
        />
      )}
    </>
  );
};
